// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { connect } from 'react-redux';
import { RootState } from 'admin/core/store';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import * as formErrorsActions from 'common/modules/app/formErrors/actions';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import {
    ipRule,
    validate,
    requiredRule,
    isIp,
} from 'common/validator';
import {
    CodeEditor,
    Form,
    FormField,
    FormFieldText,
    Section,
    setIn,
    Translate,
} from '@plesk/ui-library';
import { SegmentedControl } from 'common/components/SegmentedControl/SegmentedControl';
import {
    INTENT_TYPE,
    SIZE,
} from 'common/constants';
import { Button } from 'admin/common/components/Button/Button';
import { nestStringProperties } from 'common/modules/app/formErrors/selectors';
import * as vpcNetworkActions from 'common/modules/vpcNetwork/actions';
import { StyledCodeEditor } from 'admin/common/styles/CodeEditor';
import validator from 'validator';
import {
    IVpcNetworkAddIpRequest,
    IVpcNetworkAddIpsRequest,
} from 'common/api/resources/VpcNetwork';

interface IVpcNetworkAddIpFromProps {
    id: number;
    onSubmit: () => void;
}

export type VpcNetworkAddIpFormProps =
    IVpcNetworkAddIpFromProps &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

enum Mode {
    SINGLE = 'single',
    MULTIPLE = 'multiple',
}

const modeButtons = [
    {
        title: <Translate content="vpcNetwork.ip.form.single" />,
        value: Mode.SINGLE,
    },
    {
        title: <Translate content="vpcNetwork.ip.form.multiple" />,
        value: Mode.MULTIPLE,
    },
];

const ipsValueToRequest = (value: string): string[] => value
    .trim()
    .split('\n')
    .map(ip => ip.trim())
    .filter(ip => ip !== '');

export const ipsRule = (message: JSX.Element, version?: validator.IPVersion) => ({
    validator: (value: string) => ipsValueToRequest(value).every(ip => isIp(ip, version)),
    message,
    comparison: false,
});

export const VpcNetworkAddIpForm: React.FC<VpcNetworkAddIpFormProps> = ({
    id,
    onSubmit,
    isIpAdding,
    formErrors,
    vpcNetworkActions: {
        addIpToVpcNetwork,
        addIpsToVpcNetwork,
    },
    formErrorsActions: {
        setFormErrors,
        clearFormErrors,
    },
}) => {
    const [submitValues, setSubmitValues] = React.useState({
        ip: '',
        ips: '',
        comment: '',
    });

    const [selectedMode, setSelectedMode] = React.useState<Mode>(Mode.SINGLE);

    React.useEffect(() => () => {
        clearFormErrors();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSubmitIp = async () => {
        const rules = {
            ip: [
                requiredRule(<Translate content="validate.fieldRequired" />),
                ipRule(<Translate content="validate.badIpAddress" />, 4),
            ],
        };

        const data: IVpcNetworkAddIpRequest = {
            ip: submitValues.ip,
        };

        const errors = validate<IVpcNetworkAddIpRequest>(data, rules);

        if (Object.keys(errors).length) {
            setFormErrors(errors);
            return;
        }

        await addIpToVpcNetwork(id, data);
        onSubmit();
    };

    const handleSubmitIps = async () => {
        const rules = {
            ips: [
                requiredRule(<Translate content="validate.fieldRequired" />),
                ipsRule(<Translate content="validate.badIpAddresses" />, 4),
            ],
        };

        const errors = validate({ ips: submitValues.ips }, rules);

        if (Object.keys(errors).length) {
            setFormErrors(errors);
            return;
        }

        const data: IVpcNetworkAddIpsRequest = {
            ips: ipsValueToRequest(submitValues.ips),
        };

        await addIpsToVpcNetwork(id, data);
        onSubmit();
    };

    const handleFieldChange = (field: string, value: string) => setSubmitValues(setIn(submitValues, field, value));

    const handleChangeMode = (mode: Mode) => {
        setSelectedMode(mode);
    };

    const handleIpsChange = (value: string) => {
        setSubmitValues({
            ...submitValues,
            ips: value,
        });
    };

    return (
        <>
            <Form
                id="addIpForm"
                footerClassName="hidden"
                onSubmit={selectedMode === Mode.MULTIPLE ? handleSubmitIps : handleSubmitIp}
                onFieldChange={handleFieldChange}
                values={submitValues}
                errors={formErrors}
                hideRequiredLegend={true}
                submitButton={false}
                cancelButton={false}
                applyButton={false}
                vertical={true}
            >
                <Section>
                    <FormField>
                        <SegmentedControl
                            buttons={modeButtons}
                            selected={selectedMode}
                            onChange={handleChangeMode}
                        />
                    </FormField>
                    {selectedMode === Mode.SINGLE && (
                        <>
                            <FormFieldText
                                name="ip"
                                size={SIZE.FILL}
                                label={<Translate content="vpcNetwork.ip.form.ip" />}
                                required={true}
                            />
                        </>
                    )}
                    {selectedMode === Mode.MULTIPLE && (
                        <FormField
                            name="ips"
                            label={<Translate content="vpcNetwork.ip.form.ips" />}
                            required={true}
                        >
                            <StyledCodeEditor height={150}>
                                <CodeEditor onChange={handleIpsChange} id="ips" name="ips">
                                    {submitValues.ips}
                                </CodeEditor>
                            </StyledCodeEditor>
                        </FormField>
                    )}
                </Section>
            </Form>
            <Button
                type="submit"
                form="addIpForm"
                fill={true}
                intent={INTENT_TYPE.PRIMARY}
                size={SIZE.LG}
                isLoading={isIpAdding}
            >
                <Translate content="vpcNetwork.ip.form.saveBtn" />
            </Button>
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    formErrors: nestStringProperties(state),
    isIpAdding: state.app.loadingFlags.has(LOADING_FLAGS.VPC_NETWORK_IP_ADDING),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    vpcNetworkActions: bindActionCreators(vpcNetworkActions, dispatch),
    formErrorsActions: bindActionCreators(formErrorsActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(VpcNetworkAddIpForm);
